import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useCurrentUser } from '@/hooks/use-current-user';
import { getConsultantProfile } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DisplaySettings, GeneralSettings, HangingProtocolsSettings } from './GeneralSettings';
import { ScheduleSettings } from './ScheduleSettings';
import { SettingsConfirmDialog } from './SettingsConfirmDialog';
import { SettingsSidebar } from './SettingsSidebar';
import { ReportingSettings } from './reporting/ReportingSettings';
import { resolveSettingsCategory } from './settingsAccess';
import { TcGroupsSection } from './tc-groups/TcGroupsSection';
import { TcReportingSettings } from './tc-groups/TcReportingSettings';
import { UnsavedChangesContext, useUnsavedChangesRegistry } from './unsavedChangesGuard';
import { UsersPermissionsSection } from './users-permissions/UsersPermissionsSection';
import { ViewerSettings } from './viewer/ViewerSettings';
/**
 * Categories whose content is a workspace, not a form: user/permission tables
 * and the reporting surfaces (template editor, PDF designer + live preview).
 * These get a near-viewport dialog so the working area — especially the PDF
 * preview pane — isn't squeezed into the compact settings shell.
 */
const WIDE_CATEGORIES = new Set([
    'users_permissions',
    'reporting',
    'tc_reporting',
    'tc_groups',
]);
/*
 * Small screens get the whole screen instead of a card with a gutter.
 *
 * Phones (below sm): a 90vw x 85dvh card left most of a small screen unused,
 * so it becomes a full-width sheet. It stops short of the status bar / notch
 * (the page opts into viewport-fit=cover) by sitting on the bottom edge with
 * the top inset subtracted from its height: the primitive's close button is
 * absolutely placed near the top, so padding alone would not move it clear.
 *
 * Short landscape (a phone on its side, <=480px tall): the card was ~290px tall
 * with a 181px scroller. It takes the full height and a width inset by the
 * left/right safe areas, so neither edge sits under the sensor housing.
 */
const PHONE_SHEET = 'max-sm:top-auto max-sm:bottom-0 max-sm:translate-y-0 max-sm:h-[calc(100dvh_-_env(safe-area-inset-top))] max-sm:max-h-none max-sm:w-full max-sm:max-w-none max-sm:rounded-none max-sm:border-x-0 max-sm:border-b-0';
const SHORT_LANDSCAPE = '[@media(max-height:480px)]:h-dvh [@media(max-height:480px)]:max-h-none [@media(max-height:480px)]:w-[calc(100%_-_env(safe-area-inset-left)_-_env(safe-area-inset-right))] [@media(max-height:480px)]:max-w-none [@media(max-height:480px)]:rounded-none [@media(max-height:480px)]:border-y-0';
const SETTINGS_CONTENT = {
    general: GeneralSettings,
    display: DisplaySettings,
    viewer: ViewerSettings,
    hanging_protocols: HangingProtocolsSettings,
    reporting: ReportingSettings,
    tc_reporting: TcReportingSettings,
    schedule: ScheduleSettings,
    users_permissions: UsersPermissionsSection,
    tc_groups: TcGroupsSection,
};
export function SettingsModal({ open, onOpenChange, category, onCategoryChange, }) {
    const { t } = useTranslation('settings');
    const { canManageUsers, currentUser, isSuperAdmin } = useCurrentUser();
    const isClinicAdmin = currentUser?.clinicRoles.some((cr) => cr.roleName === 'Clinic Admin') ?? false;
    // Sections with local, unsaved edits (hanging protocols, schedule, report
    // design) register here; closing or switching category asks first instead
    // of silently unmounting them.
    const unsaved = useUnsavedChangesRegistry();
    const [pendingLeave, setPendingLeave] = useState(null);
    const guardLeave = (leave) => {
        if (unsaved.hasUnsavedChanges())
            setPendingLeave(() => leave);
        else
            leave();
    };
    // Gate on !!currentUser to avoid firing before auth completes — SettingsModal
    // is rendered by SettingsProvider (outside ProtectedRoute), so it can mount
    // before the token refresh finishes.
    const { data: tcProfile } = useQuery({
        queryKey: ['consultant-profile'],
        queryFn: getConsultantProfile,
        staleTime: 5 * 60_000,
        enabled: open && !!currentUser,
    });
    const isTcGroupAdmin = tcProfile?.groups?.some((g) => g.role === 'admin') ?? false;
    const isTcGroupMember = (tcProfile?.groups?.length ?? 0) > 0;
    // Redirect away from gated sections if user lacks permission
    const effectiveCategory = resolveSettingsCategory(category, {
        canManageUsers,
        isSuperAdmin,
        isClinicAdmin,
        isTcGroupAdmin,
        isTcGroupMember,
    });
    const ContentComponent = SETTINGS_CONTENT[effectiveCategory] ?? GeneralSettings;
    const wide = WIDE_CATEGORIES.has(effectiveCategory);
    // This modal stays mounted while closed (SettingsProvider renders it always),
    // so a confirm left pending when Settings closes or changes section some
    // other way (browser Back, a deep link) is dropped here. Otherwise it would
    // pop up again on the next open, holding the old leave action.
    const leaveScope = open ? effectiveCategory : null;
    const [pendingScope, setPendingScope] = useState(leaveScope);
    if (pendingScope !== leaveScope) {
        setPendingScope(leaveScope);
        setPendingLeave(null);
    }
    const handleOpenChange = (next) => {
        if (next)
            onOpenChange(true);
        else
            guardLeave(() => onOpenChange(false));
    };
    const handleCategoryChange = (next) => {
        if (next === effectiveCategory)
            return;
        guardLeave(() => onCategoryChange(next));
    };
    return (_jsx(Dialog, { open: open && !!currentUser, onOpenChange: handleOpenChange, children: _jsxs(DialogContent, { className: cn('flex flex-col gap-0 overflow-hidden p-0', wide
                ? 'h-[92dvh] max-h-[1000px] w-[95vw] max-w-[1600px]'
                : 'h-[85dvh] max-h-[800px] w-[90vw] max-w-[1200px]', PHONE_SHEET, SHORT_LANDSCAPE), "aria-describedby": undefined, "data-testid": "settings-modal", children: [_jsx(DialogTitle, { className: "sr-only", children: t('title') }), _jsx("div", { className: "border-border flex h-14 shrink-0 items-center border-b px-6 max-lg:[@media(max-height:480px)]:hidden", "data-testid": "settings-header", children: _jsx("h2", { className: "text-foreground text-lg font-semibold", children: t('title') }) }), _jsx(UnsavedChangesContext.Provider, { value: unsaved, children: _jsxs("div", { className: "flex min-h-0 flex-1 flex-col lg:flex-row", children: [_jsx(SettingsSidebar, { activeCategory: effectiveCategory, onCategoryChange: handleCategoryChange, className: "hidden lg:block" }), _jsx(SettingsSidebar, { activeCategory: effectiveCategory, onCategoryChange: handleCategoryChange, variant: "horizontal", className: "lg:hidden" }), _jsx("div", { className: "min-h-0 flex-1 overflow-auto p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:p-6 sm:pb-[max(1.5rem,env(safe-area-inset-bottom))]", "data-testid": "settings-content", children: _jsx(ContentComponent, {}) })] }) }), _jsx(SettingsConfirmDialog, { open: pendingLeave !== null, title: t('unsaved.title'), description: t('unsaved.body'), confirmLabel: t('unsaved.discard'), cancelLabel: t('unsaved.keepEditing'), destructive: true, onCancel: () => setPendingLeave(null), onConfirm: () => {
                        const leave = pendingLeave;
                        setPendingLeave(null);
                        unsaved.clear();
                        leave?.();
                    } })] }) }));
}
